<template>
<div>
    <Search
        v-model="planType"
        @searchInput="searchQuery"
        @orderFilter="orderFilter"
        @cleanFilter="cleanFilter"
        @categoryFilter="e => planCategory = e"
        @facetFilter="onFacetFilter"
        @yearFilter="onYearFilter"
        :plan-categories="planCategory"
        :plan-facets="planFacets"
        :selected-facets="selectedFacets"
        :available-years="availableYears"
        :selected-year="selectedYear"
        filters
        :categories="categories"
        currency
    />

    <div v-if="loaded && hasCategories && !hasActiveFilters"
        :class="['nch-plans-view-toggle', container]">
        <button
            :class="['nch-view-btn', !groupByCategory && 'nch-view-btn--active']"
            :style="!groupByCategory ? { background: color.primary, color: color.secondary } : {}"
            @click="setGroupMode(false)"
        >
            <i class="mdi mdi-view-grid"></i>
        </button>
        <button
            :class="['nch-view-btn', groupByCategory && 'nch-view-btn--active']"
            :style="groupByCategory ? { background: color.primary, color: color.secondary } : {}"
            @click="setGroupMode(true)"
        >
            <i class="mdi mdi-view-list"></i> {{ $t('cart.plans.categories') }}
        </button>
    </div>

    <!-- Grouped view by category -->
    <template v-if="loaded && groupByCategory && hasCategories && !hasActiveFilters">
        <div
            v-for="group in plansByCategory"
            :key="group.category.id || 'none'"
            class="nch-category-block"
            :style="group.category.color ? { borderLeftColor: group.category.color } : { borderLeftColor: color.primary }"
        >
            <div class="nch-category-block__header">
                <span class="nch-category-block__dot"
                    :style="{ background: group.category.color || color.primary }">
                </span>
                <span class="nch-category-block__title"
                    :style="{ color: group.category.color || color.primary }">
                    {{ group.category.id ? $findTranslation(group.category, language).name : $t('common.others') }}
                </span>
                <span class="nch-category-block__count"
                    :style="{ background: group.category.color || color.primary }">
                    {{ group.plans.length }}
                </span>
            </div>
            <div class="d-flex flex-wrap align-items-stretch" :class="[container]">
                <PlansInfo
                    v-for="plan in group.plans"
                    :key="plan.id"
                    :plan="plan"
                    :plans="group.plans.length"
                    :color="color"
                    :layout="layout"
                    :proceed="proceed"
                    :category-color="group.category.color || null"
                    @reload="getProducts"
                    @isCart="addCart"
                />
            </div>
        </div>
    </template>

    <!-- Flat view (default when filters active or grouping off) -->
    <div
        v-else-if="loaded"
        class="d-flex flex-wrap align-items-stretch nch-mt-5"
        :class="[container, isFixed]"
    >
        <PlansInfo
            v-for="plan in plans"
            :key="plan.id"
            :plan="plan"
            :plans="data.data.length"
            :color="color"
            :layout="layout"
            :proceed="proceed"
            :category-color="groupByCategory ? (plan.category_color || getCategoryColor(plan)) : null"
            @reload="getProducts"
            @isCart="addCart"
        />
    </div>

    <Empty
        v-if="empty"
        :color="color"
        class="mt-9"
        icon="mdi mdi-package-variant"
    >
        <template #title><h1 class="font-weight-medium mb-0">
            {{ $t('empty.filter_title') }}
        </h1></template>
        <template #subtitle><p>{{ $t('empty.filter_subtitle') }}.</p></template>
    </Empty>
    <div v-if="!empty && (!groupByCategory || hasActiveFilters)"
        class="container d-flex justify-content-center align-items-center my-5">
        <base-pagination
            class="mb-0"
            v-if="meta"
            :page-count="meta.last_page"
            v-model="meta.current_page"
            @input="onChangePage"
            type="#525F7F"
        />
    </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';

import PlansInfo from './PlansComponents/PlansInfo';
import BasePagination from '../argon/components/BasePagination';
import Empty from './Common/Empty';
import Search from './Search';
import debounce from '../helpers/helpers';

import Modal from '../argon/components/Modal';
import BaseButton from '../argon/components/BaseButton';
import CartAddRecurrence from './CartComponents/CartAddRecurrence';

export default {
    name: 'Plans',
    components: {
        BasePagination,
        PlansInfo,
        Search,
        Empty,
        Modal,
        BaseButton,
        CartAddRecurrence,
    },

    props: {
        categories: Array,
        planFacets: { type: Array, default: () => [] },
        availableYears: { type: Array, default: () => [] },
        color: {
            type: Object,
            required: true,
        },
        layout: {
            type: Object,
            required: true,
        },
        token: String,
    },

    data() {
        return {
            data: {
                data: [],
            },
            perPage: 6,
            search: '',
            planType: [],
            planCategory: [],
            selectedFacets: {},
            selectedYear: '',
            order: '',
            empty: true,
            loaded: false,
            modal: false,
            proceed: false,
            groupByCategory: !!this.layout.show_category_groups,
        };
    },

    computed: {
        ...mapGetters({
            language: 'LANGUAGE',
            currency: 'CURRENCY',
            message: 'COUPON_MESSAGE',
        }),

        plans() { return this.data.data || []; },

        meta() { return this.data.meta; },

        isFixed() { return this.layout.fixed ? 'isFixed' : null; },

        container() {
            if (this.$mq === 'sm' || this.$mq === 'md') {
                return 'container-fluid';
            }
            return 'container';
        },

        hasCategories() {
            return this.categories && this.categories.length > 0;
        },

        hasActiveFilters() {
            return this.search || this.planType.length > 0 || this.planCategory.length > 0 || this.order || Object.keys(this.selectedFacets).length > 0 || !!this.selectedYear;
        },

        plansByCategory() {
            const groups = {};
            this.plans.forEach(plan => {
                const catId = plan.category_id || 'none';
                if (!groups[catId]) {
                    const cat = catId === 'none'
                        ? { id: null, color: null, translations: [] }
                        : (this.categories || []).find(c => c.id == catId) || { id: catId, color: null, translations: [] };
                    groups[catId] = { category: cat, plans: [] };
                }
                groups[catId].plans.push(plan);
            });
            return Object.values(groups).sort((a, b) => {
                if (a.category.id === null) return 1;
                if (b.category.id === null) return -1;
                return 0;
            });
        },

    },

    watch: {
        search(newVal) {
            debounce(() => {
                this.getProducts(newVal, this.planType);
            }, 500).call();
        },
        planType(newVal) {
            debounce(() => {
                this.data.meta.current_page = 1;
                this.getProducts(this.search, newVal);
            }, 300).call();
        },
        planCategory(newVal) {
            debounce(() => {
                this.data.meta.current_page = 1;
                this.getProducts(this.search);
            }, 300).call();
        },
        selectedFacets: {
            deep: true,
            handler() {
                debounce(() => {
                    if (this.meta) this.data.meta.current_page = 1;
                    this.getProducts(this.search);
                }, 300).call();
            },
        },
        selectedYear() {
            debounce(() => {
                if (this.meta) this.data.meta.current_page = 1;
                this.getProducts(this.search);
            }, 300).call();
        },
        order(newVal, oldVal) {
            debounce(() => {
                if (newVal !== oldVal) {
                    this.getProducts(this.search, this.planType, newVal);
                }
            }, 200).call();
        },
        currency(newVal) {
            debounce(() => {
                this.getProducts(this.search, this.planType, newVal);
            }, 100).call();
        },
        plans() {
            this.empty = !this.plans.length;
        },
        message: {
            deep: true,
            handler(value) {
                if (value.type === 0) {
                    this.$notify({
                        type: 'success',
                        message: value.text,
                    });
                } else {
                    this.$notify({
                        type: 'danger',
                        message: value.text,
                    });
                }
            },
        },
    },

    methods: {
        getCategoryColor(plan) {
            if (!plan.category_id || !this.categories) return null;
            const category = this.categories.find(c => c.id == plan.category_id);
            return category ? category.color : null;
        },
        setGroupMode(val) {
            this.groupByCategory = val;
            this.getProducts(this.search, this.planType, this.order);
        },
        orderFilter(value) {
            this.order = value;
        },
        searchQuery(value) {
            this.search = value;
        },
        getProducts(query, type, order, page = 1) {
            this.$loading.show();
            const useGrouped = this.groupByCategory && this.hasCategories && !this.hasActiveFilters;
            const perPage = useGrouped ? 200 : this.perPage;
            window.axios
                .get(`/api/public/plans/${this.language.iso}/${perPage}`, {
                    params: {
                        page: useGrouped ? 1 : (this.meta ? this.meta.current_page : 1),
                        column: this.order ? 'plans.value' : 'plans.id',
                        order: this.order ? this.order : 'ASC',
                        currency: this.currency.code ? this.currency.code : null,
                        type: this.planType.length > 0 ? this.planType : null,
                        categories: this.planCategory.length > 0 ? this.planCategory : null,
                        facets: Object.keys(this.selectedFacets).length ? this.selectedFacets : null,
                        year: this.selectedYear || null,
                        search: query,
                    },
                })
                .then((res) => {
                    this.data = res.data;
                })
                .catch(err => console.error(err))
                .finally(() => {
                    this.$loading.close();
                    this.loaded = true;
                });
        },
        onChangePage(page) {
            this.meta.current_page = page;
            this.getProducts();
        },
        cleanFilter() {
            this.order = '';
            this.planType = [];
            this.planCategory = [];
            this.selectedFacets = {};
            this.selectedYear = '';
            this.search = '';
            this.getProducts();
        },
        onFacetFilter(facets) {
            this.selectedFacets = facets;
        },
        onYearFilter(year) {
            this.selectedYear = year;
        },
        addCart(id) {
            this.plans.map((plan) => {
                if (plan.id === id) {
                    return { ...plan, isCart: true };
                }
                return plan;
            });
        },
    },
    beforeMount() {
        if (this.token) {
            localStorage.setItem("nochalks_token", this.token);
        }
    },
    mounted() {
        this.$store.dispatch('GET_SESSION_CURRENCY');
    },
};
</script>

<style scoped>
.isFixed {
    margin-top: 122px !important;
}
.nch-mt-5 {
    margin-top: 15px;
}

/* View toggle */
.nch-plans-view-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 0 0.25rem;
}
.nch-view-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.85rem;
    border-radius: 20px;
    border: 1px solid #ddd;
    background: transparent;
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    color: #666;
    transition: all 0.2s;
}
.nch-view-btn--active {
    border-color: transparent;
    font-weight: 600;
}
.nch-view-btn:hover:not(.nch-view-btn--active) {
    border-color: #aaa;
    color: #333;
}

/* Category blocks */
.nch-category-block {
    border-left: 4px solid #ccc;
    margin: 1.5rem 0;
    padding-left: 0;
}
.nch-category-block__header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 1.25rem 0.5rem;
}
.nch-category-block__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
}
.nch-category-block__title {
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}
.nch-category-block__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    border-radius: 11px;
    padding: 0 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #fff;
}
</style>
